import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormControl,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import type { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { textStyle } from "../../styles/Style";
import "../../styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import {
  validateSpace,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  reserveServiceChargeFieldErrors,
  reserveServiceChargeFieldHelperText,
} from "../../../constants/reserveServiceChargeValidationHelperText";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { ReservePropertyEnum } from "../../../constants/NavbarList";
import {
  reserveListAvailableHeadingTooltip,
  reserveServiceChargeTooltip,
} from "../../../constants/ReserveTooltipInformation";
import useScrollToTop from "../../hooks/useScrollToTop";
import {
  dayjsStartOfDayToIso,
  storedToDayjsOrNull,
} from "../../../constants/PropertyInformation";

const ReserveServiceCharge = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [taxTypeValue, setTaxTypeValue] = useState<string | null>(null);
  const [valueField, setValueField] = useState<string | null>(null);
  const [calculationType, setCalculationType] = useState<string>("");
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [taxTypeError, setTaxTypeError] = useState<boolean>(false);
  const [valueError, setValueError] = useState<boolean>(false);
  const [serviceChargeInformation, setServiceChargeInformation] = useState<
    any[]
  >([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const typeTrim = (taxTypeValue ?? "").trim();
  const valueTrim = (valueField ?? "").trim();
  const calculationTrim = (calculationType ?? "").trim();
  const payload = {
    "Tax Type": typeTrim,
    Value: valueTrim,
    "Calculation Type": calculationTrim,
    "Start Date": dayjsStartOfDayToIso(startDate),
    "End Date": dayjsStartOfDayToIso(endDate),
    Code: typeTrim,
    Name: valueTrim,
  };

  const addServiceCharge = () => {
    if (!validateInput()) return;
    const rawType = taxTypeValue;
    if (checkDuplicate(serviceChargeInformation ?? [], "Tax Type", rawType)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
    } else {
      setServiceChargeInformation((prev: any) =>
        updateData(
          Array.isArray(prev) && prev.length
            ? [
                ...prev,
                {
                  id: prev.length + 1,
                  ...payload,
                },
              ]
            : [{ id: 1, ...payload }],
        ),
      );
      setLastUpdatedRecord(payload);
      editServiceCharge(null);
      setEditClicked(false);
    }
  };

  const editServiceCharge = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    if (list == null) {
      setTaxTypeValue(null);
      setValueField(null);
      setCalculationType("");
      setStartDate(null);
      setEndDate(null);
      setTaxTypeError(false);
      setValueError(false);
      return;
    }
    setTaxTypeValue(
      list["Tax Type"] != null && String(list["Tax Type"]).length > 0
        ? String(list["Tax Type"])
        : list["Tax Name"] != null
          ? String(list["Tax Name"])
          : String(list["Code"] ?? ""),
    );
    setValueField(
      list["Value"] !== undefined && list["Value"] !== null
        ? String(list["Value"])
        : null,
    );
    const ct = list["Calculation Type"];
    setCalculationType(
      ct != null && String(ct).trim() !== "" ? String(ct).trim() : "",
    );
    setStartDate(storedToDayjsOrNull(list["Start Date"]));
    setEndDate(storedToDayjsOrNull(list["End Date"]));
    setTaxTypeError(false);
    setValueError(false);
  };

  const updateServiceCharge = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        (serviceChargeInformation ?? []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        "Tax Type",
        typeTrim,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setServiceChargeInformation((prev: any) =>
      (Array.isArray(prev) ? prev : []).map((item: any) =>
        String(item.id) === String(selectedId) ? { ...item, ...payload } : item,
      ),
    );
    editServiceCharge(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${ReservePropertyEnum.serviceCharge} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const deleteServiceCharge = (id: number) => {
    setServiceChargeInformation(deleteItem(serviceChargeInformation ?? [], id));
  };

  const validateInput = () => {
    setTaxTypeError(false);
    setValueError(false);

    let ok = true;
    const textMax = validationLength.departmentName;

    if (!typeTrim) {
      setTaxTypeError(true);
      ok = false;
    } else if (!validateSpace(typeTrim, textMax, validationType.OTHER)) {
      setTaxTypeError(true);
      ok = false;
    }

    if (valueTrim && !validateSpace(valueTrim, textMax, validationType.OTHER)) {
      setValueError(true);
      ok = false;
    }

    if (!ok) {
      if (!typeTrim) {
        setAlertMessage("Service Charge is mandatory");
      } else {
        setAlertMessage("Please correct the highlighted fields");
      }
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    setServiceChargeInformation(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(serviceChargeInformation);
    editClicked &&
      setSelectedId(
        checkData(serviceChargeInformation, selectedId) ? selectedId : null,
      );
  }, [serviceChargeInformation]);

  useEffect(() => {
    if (typeTrim) {
      setTaxTypeError(
        !validateSpace(
          typeTrim,
          validationLength.departmentName,
          validationType.OTHER,
        ),
      );
    } else {
      setTaxTypeError(false);
    }
  }, [taxTypeValue]);

  const serviceChargeHelperText = taxTypeError
    ? !typeTrim
      ? reserveServiceChargeFieldErrors.required
      : reserveServiceChargeFieldErrors.serviceCharge
    : reserveServiceChargeFieldHelperText.serviceCharge;

  const valueHelperText = valueError
    ? reserveServiceChargeFieldErrors.value
    : reserveServiceChargeFieldHelperText.value;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction="column" gap="10px">
        <div ref={topRef} />
        <Stack direction="column" width="100%">
          <Paper
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              padding: "20px",
            }}
          >
            <Stack
              direction="row"
              width="100%"
              gap="10px"
              alignItems="center"
              justifyContent="space-between"
              mb={4}
            >
              <Typography variant="h6" component="div" sx={textStyle}>
                {ReservePropertyEnum.serviceCharge}
                <Tooltip
                  title={tooltipComponent(reserveServiceChargeTooltip.header)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Typography>
              <Stack direction="row" gap="10px" justifyContent="space-between">
                {editClicked && (
                  <ClearButton
                    click={() => {
                      editServiceCharge(null);
                      setSelectedId(null);
                    }}
                  />
                )}
                {lastUpdatedRecord && (
                  <CopyButton
                    click={() => {
                      editServiceCharge(lastUpdatedRecord);
                    }}
                  />
                )}
              </Stack>
            </Stack>

            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Service Charge
                <Tooltip
                  title={tooltipComponent(reserveServiceChargeTooltip.taxType)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="reserve-service-charge-type"
                variant="outlined"
                hiddenLabel
                placeholder="Service charge"
                helperText={serviceChargeHelperText}
                value={taxTypeValue ?? ""}
                fullWidth
                error={taxTypeError}
                onChange={(e: any) => {
                  setTaxTypeValue(e.target.value);
                  setTaxTypeError(false);
                }}
                inputProps={{ "aria-label": "Tax Type" }}
              />
            </Stack>

            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Value
                <Tooltip
                  title={tooltipComponent(reserveServiceChargeTooltip.value)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="reserve-service-charge-value"
                variant="outlined"
                hiddenLabel
                placeholder="Value"
                value={valueField ?? ""}
                helperText={valueHelperText}
                error={valueError}
                fullWidth
                onChange={(e: any) => {
                  setValueField(e.target.value);
                  setValueError(false);
                }}
                inputProps={{ "aria-label": "Value" }}
              />
            </Stack>

            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Calculation Type
                <Tooltip
                  title={tooltipComponent(
                    reserveServiceChargeTooltip.calculationType,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <FormControl fullWidth size="small">
                <InputLabel id="reserve-service-charge-calc-label">
                  Calculation Type
                </InputLabel>
                <Select
                  labelId="reserve-service-charge-calc-label"
                  id="reserve-service-charge-calc"
                  label="Calculation Type"
                  value={calculationType}
                  onChange={(e) => setCalculationType(e.target.value as string)}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Select…</em>
                  </MenuItem>
                  <MenuItem value="Flat">Flat</MenuItem>
                  <MenuItem value="Percentage">Percentage</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Stack
              className="form-checkbox-row form-two-col-row"
              direction="row"
              width="100%"
              gap="20px"
              flexWrap="wrap"
            >
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Start Date
                  <Tooltip
                    title={tooltipComponent(
                      reserveServiceChargeTooltip.startDate,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <DatePicker
                  value={startDate}
                  onChange={(v) => setStartDate(v)}
                  slotProps={{
                    textField: { fullWidth: true, size: "small" },
                  }}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  End Date
                  <Tooltip
                    title={tooltipComponent(
                      reserveServiceChargeTooltip.endDate,
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <DatePicker
                  value={endDate}
                  onChange={(v) => setEndDate(v)}
                  slotProps={{
                    textField: { fullWidth: true, size: "small" },
                  }}
                />
              </Stack>
            </Stack>
            <Stack className="form-two-col-row" direction="row" width="100%">
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                }}
                id="reserve-service-charge-clear-dates"
              >
                Clear selected dates
              </Button>
            </Stack>

            <Stack direction="row" width="100%" justifyContent="flex-end">
              <Button
                variant="contained"
                sx={{
                  cursor: "pointer",
                  border: "1px solid gray",
                  borderRadius: "10px",
                  background: "darkgreen",
                  color: "white",
                }}
                onClick={selectedId ? updateServiceCharge : addServiceCharge}
                id="addReserveServiceCharge"
              >
                {selectedId ? buttonValues.UPDATE : buttonValues.ADD}{" "}
                {ReservePropertyEnum.serviceCharge}
              </Button>
            </Stack>

            <Stack direction="column" gap="10px" mt={4}>
              <Typography
                variant="h6"
                component="div"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                {ReservePropertyEnum.serviceCharge} available
                <Tooltip
                  title={tooltipComponent(reserveListAvailableHeadingTooltip)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "red", fontWeight: 800 }}
              >
                Do not use sample data. Those are for demo purposes only!
              </Typography>
              <Stack direction="column" gap="10px">
                {Array.isArray(serviceChargeInformation) &&
                serviceChargeInformation.length > 0 ? (
                  <DraggableItemList
                    itemData={serviceChargeInformation}
                    onOrderUpdate={(order) =>
                      setServiceChargeInformation(order)
                    }
                    onEdit={(info) => {
                      editServiceCharge(info);
                      scrollToTop();
                    }}
                    onDelete={(id) => deleteServiceCharge(id)}
                    code="Code"
                    name="Name"
                  />
                ) : (
                  <Typography variant="body2">
                    No {ReservePropertyEnum.serviceCharge} information available
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Stack>
        <AlertComponent
          message={alertMessage}
          type={alertType}
          alertOpen={openAlert}
          closeAlert={() => setOpenAlert(false)}
        />
      </Stack>
    </LocalizationProvider>
  );
};

export default ReserveServiceCharge;
